import React, { useRef, useEffect, useImperativeHandle, forwardRef } from 'react';
// Não importamos 'bootstrap' diretamente, assumimos que está carregado globalmente via <script>

// AlertModal é um componente de modal reutilizável que pode funcionar como alerta simples
// ou como uma caixa de diálogo de confirmação.
// Ele usa forwardRef para permitir que o componente pai (GameContext)
// acesse seus métodos show/hide diretamente.
const AlertModal = forwardRef(({ title, message, isConfirmation, onConfirm, onClose, confirmButtonText, cancelButtonText }, ref) => {
  const modalElement = useRef(null); // Referência para o elemento DOM do modal
  const bsModalInstance = useRef(null); // Referência para armazenar a instância do modal do Bootstrap

  // Efeito para inicializar a instância do modal do Bootstrap quando o componente é montado
  useEffect(() => {
    // Garante que o elemento DOM do modal exista e que o objeto 'bootstrap' esteja disponível globalmente
    // Este efeito deve ser executado APENAS UMA VEZ para criar a instância do modal.
    if (modalElement.current && window.bootstrap) {
      bsModalInstance.current = new window.bootstrap.Modal(modalElement.current, {
        backdrop: 'static',
        keyboard: false
      });

      // Função de limpeza para descartar a instância do modal quando o componente é desmontado
      return () => {
        if (bsModalInstance.current) {
          bsModalInstance.current.dispose();
          bsModalInstance.current = null; // Limpa a referência
        }
      };
    }
  }, []); // Dependência vazia: executa UMA VEZ na montagem

  // Permite que o componente pai chame show() e hide() na instância do modal
  useImperativeHandle(ref, () => ({
    show: () => {
      bsModalInstance.current?.show(); // Exibe o modal usando a instância na ref
    },
    hide: () => {
      bsModalInstance.current?.hide(); // Oculta o modal usando a instância na ref
    }
  }), []); // Dependência vazia: as funções show/hide são estáveis e sempre usarão bsModalInstance.current

  // Lida com o clique no botão de confirmação ("Sim" ou "OK")
  const handleConfirm = () => {
    bsModalInstance.current?.hide();
    onConfirm?.();
  };

  // Lida com o clique no botão de fechar ("Não" ou "Fechar")
  const handleClose = () => {
    bsModalInstance.current?.hide();
    onClose?.();
  };

  return (
    <div className="modal fade" ref={modalElement} tabIndex="-1" aria-labelledby="alertModalLabel" aria-hidden="true">
      <div className="modal-dialog modal-dialog-centered">
        {/* Adicionado classes bg-dark e text-light para melhorar o contraste, aplicado ao modal-content */}
        <div className="modal-content bg-dark text-light">
          {/* Reforçado bg-dark e text-light no modal-header e adicionado border-secondary */}
          <div className="modal-header bg-dark text-light border-bottom border-secondary">
            <h5 className="modal-title" id="alertModalLabel">{title}</h5>
            {!isConfirmation && (
                <> {/* NOVO: Adicionado React.Fragment para envolver o comentário e o botão */}
                    {/* btn-close-white para ícone de fechar branco em fundo escuro */}
                    <button type="button" className="btn-close btn-close-white" onClick={handleClose} aria-label="Close"></button>
                </>
            )}
          </div>
          {/* Adicionado text-light no modal-body para garantir a cor do texto */}
          <div className="modal-body text-light">
            {message}
          </div>
          {/* Reforçado bg-dark e text-light no modal-footer e adicionado border-secondary */}
          <div className="modal-footer bg-dark text-light border-top border-secondary">
            {isConfirmation ? (
              <>
                {/* ORDEM INVERTIDA: Botão "Sim" primeiro, depois "Não" */}
                <button type="button" className="btn btn-primary" onClick={handleConfirm}>
                  {confirmButtonText || 'Sim'}
                </button>
                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                  {cancelButtonText || 'Não'}
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-primary" onClick={handleConfirm}>
                {confirmButtonText || 'OK'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default AlertModal;
